
import Vue, { CreateElement } from 'vue';

import WebText from '../Text/Text.vue';
import VARIANT, { Variant } from './ButtonType/constant/variant';
import { colors, spacings } from '../Token/token';
import { getButtonStyle } from './ButtonStyle';
import { appendPx } from '../Token/helper/tokenHelper';
import { Row } from '../Grid';
import elevation from '../Card/elevation';

/**
 * > *This component is deprecated, please go to V2 > Button*
 *
 * Button component
 *
 * Variant key list (From left to right based on story below): <br />
 * `primary` | `primary-green` | `primary-dusk-green` | `main-cta`
 * | `secondary-main` | `secondary` | `text` | `text-underlined`
 * | `text-black` | `text-white` | `text-destructive` | `destructive-primary`
 * | `destructive-secondary` | `circular-primary` | `circular-primary-green`
 * | `circular-main-cta` | `circular-outline-black` | `circular-outline-primary`
 * | `circular-outline-white` | `outline-black` | `outline-primary-greeb`
 * | `circular-outline-primary` | `outline-white` | `outline-radio-inactive`
 * | `radio-primary-green` | `radio-outline-not-selected`
 *
 * This components receive default `slot` - The content of the button
 */
export default Vue.extend({
  name: 'Button',
  inheritAttrs: false,
  components: {
    WebText,
    Row
  },
  props: {
    /**
     * Type of the button.<br />
     * Check to the Variant section in the top for possible value.
     */
    variant: {
      type: String,
      default: VARIANT.PRIMARY,
      validator: (variant: string) => Object.values(VARIANT).includes(variant)
    },
    /**
     * The left icon SVG component in button.
     * @type {VueComponent}
     */
    leftIcon: {
      default: null
    },
    /**
     * The right icon SVG component in button.
     * @type {VueComponent}
     */
    rightIcon: {
      default: null
    },
    /**
     * The left icon color fill.
     * @type {VueComponent}
     */
    leftIconFill: {
      type: String,
      default: null
    },
    /**
     * Shadow of the button.<br />
     * Possible value `flat` | `container` | `interactive`
     * | `float` | `hover`<br />
     * From left to right, shadow will be bigger
     */
    elevation: {
      type: String,
      default: elevation.flat
    },
    /**
     * The button is only icon, no text.
     */
    onlyIcon: {
      type: Boolean,
      default: false
    },
    /**
     * Dividing button body section in 2 columns.
     * (only works when right/left icon is applied)
     */
    twoItems: {
      type: Boolean
    },
    /**
     * Inline style
     */
    styles: {
      type: Object,
      default: null
    },
    /**
     * Make the button disabled (un-clickable).
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Make the button loading.
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Text variation in the button.<br />
     * Refer to WebText
     */
    textVariant: {
      type: String,
      default: 'ui-baseline'
    },
    /**
     * Custom border radius of the button.
     */
    borderRadius: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      mouseDown: false
    };
  },
  computed: {
    buttonStyle() {
      let style = getButtonStyle(this.variant, this.elevation);
      if (this.disabled) {
        return {
          backgroundColor: '#cecfd0',
          borderRadius: '4px',
          border: 'none'
        };
      }
      if (this.onlyIcon) {
        style.paddingRight = appendPx(spacings.xs);
        style.paddingLeft = appendPx(spacings.xs);
      }
      if (!!this.borderRadius) {
        style.borderRadius = this.borderRadius;
      }
      if (this.styles && typeof this.styles === 'object') {
        style = {
          ...style,
          ...this.styles
        };
      }
      return style;
    },
    hasIcon() {
      return !!this.leftIcon || !!this.rightIcon;
    },
    isText() {
      return this.variant.indexOf('text-') !== -1;
    }
  },
  methods: {
    handleOnNativeClick(e: Event) {
      if (this.disabled) {
        return null;
      } else if (this.loading) {
        return null;
      } else {
        /**
         * Handle when user click the button.
         * @event click
         * @type {function}
         */
        return this.$emit('click', e);
      }
    },
    handleMouseDown() {
      // @ts-ignore
      this.mouseDown = true;
    },
    handleMouseUp() {
      // @ts-ignore
      this.mouseDown = false;
    }
  }
});
